/**
 * Report Generator Service
 * Generates PDF and DOCX reports from template data
 * 
 * R2.1: Uses server-side chart rendering for consistent output
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  HeadingLevel,
  PageBreak,
  BorderStyle,
  ShadingType,
  ImageRun,
} from 'docx';
import { renderChartServerSide } from '../api';

// PDF Generator
export class PDFGenerator {
  constructor(reportData) {
    this.reportData = reportData;
    this.doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.margins = { top: 20, right: 20, bottom: 20, left: 20 };
    this.contentWidth = this.pageWidth - this.margins.left - this.margins.right;
    this.currentY = this.margins.top;
    this.pageNumber = 1;
    this.branding = reportData.branding || {};
  }

  // Convert hex color to RGB array
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : [30, 64, 175]; // Default blue
  }

  // Add new page
  addPage() {
    this.doc.addPage();
    this.pageNumber++;
    this.currentY = this.margins.top;
    this.addHeader();
    this.addFooter();
  }

  // Check if we need a new page
  checkPageBreak(neededHeight = 30) {
    if (this.currentY + neededHeight > this.pageHeight - this.margins.bottom - 15) {
      this.addPage();
    }
  }

  // Add header to page
  addHeader() {
    if (this.pageNumber > 1) {
      const { options } = this.reportData;
      if (options?.header_footer?.show_header) {
        this.doc.setFontSize(9);
        this.doc.setTextColor(128, 128, 128);
        this.doc.text(
          options.header_footer.header_text || this.reportData.meta?.plan_name || '',
          this.margins.left,
          12
        );
        // Add line under header
        this.doc.setDrawColor(200, 200, 200);
        this.doc.line(this.margins.left, 15, this.pageWidth - this.margins.right, 15);
      }
    }
  }

  // Add footer to page
  addFooter() {
    const { options } = this.reportData;
    if (options?.header_footer?.show_footer !== false) {
      this.doc.setFontSize(9);
      this.doc.setTextColor(128, 128, 128);
      
      // Page number
      if (options?.header_footer?.show_page_numbers !== false) {
        this.doc.text(
          `Page ${this.pageNumber}`,
          this.pageWidth - this.margins.right - 15,
          this.pageHeight - 10
        );
      }
      
      // Footer text
      const footerText = options?.header_footer?.footer_text || 
                         `Generated on ${new Date().toLocaleDateString()}`;
      this.doc.text(footerText, this.margins.left, this.pageHeight - 10);
    }
  }

  // Render cover page
  renderCoverPage() {
    const { cover_page, branding } = this.reportData;
    if (!cover_page?.enabled) return;

    const primaryColor = this.hexToRgb(branding?.primary_color || '#1e40af');

    // Background accent
    this.doc.setFillColor(...primaryColor);
    this.doc.rect(0, 0, this.pageWidth, 60, 'F');

    // Company name
    this.doc.setTextColor(255, 255, 255);
    this.doc.setFontSize(14);
    this.doc.text(branding?.company_name || 'Company', this.margins.left, 30);

    // Title
    this.doc.setTextColor(50, 50, 50);
    this.doc.setFontSize(32);
    this.doc.setFont(undefined, 'bold');
    this.doc.text(
      cover_page.title || 'Business Plan',
      this.pageWidth / 2,
      this.pageHeight / 2 - 20,
      { align: 'center' }
    );

    // Subtitle
    this.doc.setFontSize(16);
    this.doc.setFont(undefined, 'normal');
    this.doc.setTextColor(100, 100, 100);
    this.doc.text(
      cover_page.subtitle || '',
      this.pageWidth / 2,
      this.pageHeight / 2,
      { align: 'center' }
    );

    // Date
    if (cover_page.show_date) {
      this.doc.setFontSize(12);
      this.doc.text(
        cover_page.date || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        this.pageWidth / 2,
        this.pageHeight / 2 + 20,
        { align: 'center' }
      );
    }

    // Bottom bar
    this.doc.setFillColor(...primaryColor);
    this.doc.rect(0, this.pageHeight - 20, this.pageWidth, 20, 'F');

    this.addPage();
  }

  // Render table of contents
  renderTableOfContents(toc) {
    this.doc.setFontSize(24);
    this.doc.setFont(undefined, 'bold');
    this.doc.setTextColor(50, 50, 50);
    this.doc.text('Table of Contents', this.margins.left, this.currentY);
    this.currentY += 15;

    this.doc.setFontSize(11);
    this.doc.setFont(undefined, 'normal');

    toc.forEach((item, index) => {
      this.checkPageBreak(10);
      
      // Title
      this.doc.setTextColor(50, 50, 50);
      this.doc.text(item.title, this.margins.left, this.currentY);
      
      // Dots
      const titleWidth = this.doc.getTextWidth(item.title);
      const pageNumWidth = this.doc.getTextWidth(String(item.page));
      const dotsStart = this.margins.left + titleWidth + 5;
      const dotsEnd = this.pageWidth - this.margins.right - pageNumWidth - 5;
      
      this.doc.setTextColor(180, 180, 180);
      let dotX = dotsStart;
      while (dotX < dotsEnd) {
        this.doc.text('.', dotX, this.currentY);
        dotX += 3;
      }
      
      // Page number
      this.doc.setTextColor(50, 50, 50);
      this.doc.text(
        String(item.page),
        this.pageWidth - this.margins.right,
        this.currentY,
        { align: 'right' }
      );
      
      this.currentY += 8;
    });

    this.addPage();
  }

  // Render section title
  renderSectionTitle(title) {
    this.checkPageBreak(20);
    
    const primaryColor = this.hexToRgb(this.branding?.primary_color || '#1e40af');
    
    // Colored bar
    this.doc.setFillColor(...primaryColor);
    this.doc.rect(this.margins.left, this.currentY - 5, 4, 12, 'F');
    
    // Title text
    this.doc.setFontSize(18);
    this.doc.setFont(undefined, 'bold');
    this.doc.setTextColor(50, 50, 50);
    this.doc.text(title, this.margins.left + 10, this.currentY + 3);
    
    this.currentY += 15;
  }

  // Render narrative block
  renderNarrative(block) {
    if (block.title) {
      this.renderSectionTitle(block.title);
    }

    // Use AI-generated content first, then fall back to custom content
    const content = block.ai_content || block.data?.content || block.config?.custom_text || 'No content available.';
    
    this.doc.setFontSize(11);
    this.doc.setFont(undefined, 'normal');
    this.doc.setTextColor(70, 70, 70);

    // Split text into lines
    const lines = this.doc.splitTextToSize(content, this.contentWidth);
    
    lines.forEach(line => {
      this.checkPageBreak(7);
      this.doc.text(line, this.margins.left, this.currentY);
      this.currentY += 6;
    });

    this.currentY += 10;
  }

  // Render AI content description/insights
  renderAIContent(content, label = 'Analysis') {
    if (!content) return;
    
    this.checkPageBreak(20);
    
    // Small label for AI-generated content
    this.doc.setFontSize(9);
    this.doc.setFont(undefined, 'italic');
    this.doc.setTextColor(180, 150, 50);
    this.doc.text(label, this.margins.left, this.currentY);
    this.currentY += 5;
    
    // Content
    this.doc.setFontSize(10);
    this.doc.setFont(undefined, 'normal');
    this.doc.setTextColor(80, 80, 80);
    
    const lines = this.doc.splitTextToSize(content, this.contentWidth);
    lines.forEach(line => {
      this.checkPageBreak(6);
      this.doc.text(line, this.margins.left, this.currentY);
      this.currentY += 5;
    });
    
    this.currentY += 8;
  }

  // Render data table
  renderDataTable(block) {
    if (block.title) {
      this.renderSectionTitle(block.title);
    }

    // Render AI description first if available
    if (block.ai_content) {
      this.renderAIContent(block.ai_content, 'Summary');
    }

    const data = block.data;
    if (!data) {
      this.doc.setFontSize(10);
      this.doc.setTextColor(150, 150, 150);
      this.doc.text('No data available', this.margins.left, this.currentY);
      this.currentY += 15;
      return;
    }

    // Determine table structure based on source
    const source = block.config?.source || '';
    let tableData = this.formatDataForTable(data, source);

    if (tableData.headers.length > 0 && tableData.rows.length > 0) {
      const primaryColor = this.hexToRgb(this.branding?.primary_color || '#1e40af');
      
      autoTable(this.doc, {
        startY: this.currentY,
        head: [tableData.headers],
        body: tableData.rows,
        theme: 'grid',
        headStyles: {
          fillColor: primaryColor,
          textColor: [255, 255, 255],
          fontStyle: 'bold',
          fontSize: 10,
        },
        bodyStyles: {
          fontSize: 9,
          textColor: [50, 50, 50],
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        margin: { left: this.margins.left, right: this.margins.right },
        didDrawPage: (data) => {
          if (data.pageNumber > 1) {
            this.pageNumber = data.pageNumber;
            this.addFooter();
          }
        },
      });

      this.currentY = this.doc.lastAutoTable.finalY + 15;
    } else {
      this.doc.setFontSize(10);
      this.doc.setTextColor(150, 150, 150);
      this.doc.text('No tabular data available', this.margins.left, this.currentY);
      this.currentY += 15;
    }
  }

  // Format various data types into table format
  formatDataForTable(data, source) {
    // Handle null/undefined data
    if (!data) {
      return { headers: [], rows: [] };
    }

    if (source.includes('income_statement')) {
      // Handle different data structures - could be array or object
      let statements = [];
      if (Array.isArray(data.statements)) {
        statements = data.statements;
      } else if (Array.isArray(data.summary)) {
        statements = data.summary;
      } else if (Array.isArray(data)) {
        statements = data;
      } else if (typeof data === 'object' && data.revenue !== undefined) {
        // Single statement object
        statements = [data];
      } else if (typeof data === 'object') {
        // Convert object with year keys to array
        statements = Object.entries(data)
          .filter(([key]) => !isNaN(key) || key.includes('year'))
          .map(([year, values]) => ({ year, ...values }));
      }
      
      if (statements.length === 0) {
        return { headers: ['Item', 'Value'], rows: this.objectToRows(data) };
      }
      
      return {
        headers: ['Year', 'Revenue', 'Gross Profit', 'Net Income', 'Net Margin'],
        rows: statements.map(s => [
          s.year || s.period || '-',
          this.formatCurrency(s.revenue || s.total_revenue || 0),
          this.formatCurrency(s.gross_profit || 0),
          this.formatCurrency(s.net_income || 0),
          `${s.net_margin || 0}%`,
        ]),
      };
    }
    
    if (source.includes('balance_sheet')) {
      let sheets = [];
      if (Array.isArray(data.balance_sheets)) {
        sheets = data.balance_sheets;
      } else if (Array.isArray(data.summary)) {
        sheets = data.summary;
      } else if (Array.isArray(data)) {
        sheets = data;
      } else if (typeof data === 'object' && (data.assets !== undefined || data.total_assets !== undefined)) {
        sheets = [data];
      }
      
      if (sheets.length === 0) {
        return { headers: ['Item', 'Value'], rows: this.objectToRows(data) };
      }
      
      return {
        headers: ['Year', 'Total Assets', 'Total Liabilities', 'Total Equity'],
        rows: sheets.map(s => [
          s.year || s.period || '-',
          this.formatCurrency(s.assets?.total_assets || s.total_assets || 0),
          this.formatCurrency(s.liabilities?.total_liabilities || s.total_liabilities || 0),
          this.formatCurrency(s.equity?.total_equity || s.total_equity || 0),
        ]),
      };
    }
    
    if (source.includes('cash_flow')) {
      let flows = [];
      if (Array.isArray(data.cash_flows)) {
        flows = data.cash_flows;
      } else if (Array.isArray(data)) {
        flows = data;
      } else if (typeof data === 'object' && data.operating_activities !== undefined) {
        flows = [data];
      }
      
      if (flows.length === 0) {
        return { headers: ['Item', 'Value'], rows: this.objectToRows(data) };
      }
      
      return {
        headers: ['Year', 'Operating', 'Investing', 'Financing', 'Net Change'],
        rows: flows.map(f => [
          f.year || f.period || '-',
          this.formatCurrency(f.operating_activities || 0),
          this.formatCurrency(f.investing_activities || 0),
          this.formatCurrency(f.financing_activities || 0),
          this.formatCurrency(f.net_change || 0),
        ]),
      };
    }

    if (source.includes('revenue_projections') || source.includes('sales_forecasts')) {
      let projections = [];
      if (Array.isArray(data.projections)) {
        projections = data.projections;
      } else if (Array.isArray(data)) {
        projections = data;
      }
      
      if (projections.length === 0) {
        return { headers: ['Item', 'Value'], rows: this.objectToRows(data) };
      }
      
      return {
        headers: ['Year', 'Total Revenue'],
        rows: projections.map(p => [
          p.year || p.period || '-',
          this.formatCurrency(p.total_revenue || p.revenue || 0),
        ]),
      };
    }

    // Generic array to table
    if (Array.isArray(data) && data.length > 0) {
      const firstItem = data[0] || {};
      const headers = Object.keys(firstItem).slice(0, 6); // Limit columns
      return {
        headers: headers.map(h => h.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())),
        rows: data.slice(0, 20).map(item => headers.map(h => this.formatValue(item[h]))),
      };
    }

    // Generic object to table (key-value pairs)
    if (typeof data === 'object' && !Array.isArray(data)) {
      return { headers: ['Item', 'Value'], rows: this.objectToRows(data) };
    }

    return { headers: [], rows: [] };
  }

  // Convert object to key-value rows
  objectToRows(obj) {
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj)
      .filter(([key]) => !key.startsWith('_') && key !== 'error')
      .slice(0, 15)
      .map(([key, value]) => [
        key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        this.formatValue(value)
      ]);
  }

  formatCurrency(value) {
    if (typeof value !== 'number') return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  formatValue(value) {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') return value.toLocaleString();
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  // Render custom table
  renderCustomTable(block) {
    if (block.title) {
      this.renderSectionTitle(block.title);
    }

    // Add AI summary if available
    if (block.ai_content) {
      this.renderAIContent(block.ai_content, 'Summary');
    }

    const columns = block.data?.columns || block.config?.columns || [];
    const rows = block.data?.rows || block.config?.rows || [];

    if (columns.length === 0) {
      this.doc.setFontSize(10);
      this.doc.setTextColor(150, 150, 150);
      this.doc.text('Empty table', this.margins.left, this.currentY);
      this.currentY += 15;
      return;
    }

    const headers = columns.map(c => c.label || c.header || c.name || 'Column');
    const tableRows = rows.map(row =>
      columns.map(col => row[col.key] || row[col.id] || row[col.name] || '')
    );

    const primaryColor = this.hexToRgb(this.branding?.primary_color || '#1e40af');

    // Calculate if we need totals row
    const showTotals = block.config?.show_totals;
    let totalsRow = null;
    
    if (showTotals && tableRows.length > 0) {
      totalsRow = columns.map((col, index) => {
        if (index === 0) return 'Total';
        const values = tableRows.map(row => parseFloat(row[index]) || 0);
        const sum = values.reduce((a, b) => a + b, 0);
        return sum > 0 ? this.formatCurrency(sum) : '';
      });
    }

    autoTable(this.doc, {
      startY: this.currentY,
      head: [headers],
      body: tableRows.length > 0 
        ? (totalsRow ? [...tableRows, totalsRow] : tableRows)
        : [columns.map(() => '-')],
      theme: 'grid',
      headStyles: {
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      footStyles: showTotals ? {
        fillColor: [240, 240, 240],
        fontStyle: 'bold',
      } : {},
      margin: { left: this.margins.left, right: this.margins.right },
    });

    this.currentY = this.doc.lastAutoTable.finalY + 15;
  }

  // Render chart with actual data visualization
  // R2.1: Uses server-side rendering with client-side fallback
  async renderChart(block) {
    if (block.title) {
      this.renderSectionTitle(block.title);
    }

    this.checkPageBreak(90);

    const chartType = block.config?.chart_type || 'bar';
    const data = block.data;
    const dataStatus = block.data_status || data?.status;
    
    // Check data status from backend
    if (dataStatus === 'no_data' || dataStatus === 'error') {
      // Show informative placeholder with status message
      this.doc.setDrawColor(200, 200, 200);
      this.doc.setFillColor(255, 251, 235); // Amber-50 background
      this.doc.roundedRect(
        this.margins.left,
        this.currentY,
        this.contentWidth,
        40,
        3,
        3,
        'FD'
      );
      this.doc.setFontSize(10);
      this.doc.setTextColor(180, 83, 9); // Amber-700
      const message = dataStatus === 'error' 
        ? `Chart Error: ${block.error || data?.message || 'Failed to load data'}`
        : `No Data: ${block.data_message || data?.message || 'No data available for this chart'}`;
      this.doc.text(
        message,
        this.pageWidth / 2,
        this.currentY + 22,
        { align: 'center' }
      );
      this.currentY += 50;
      
      // Still show AI insights if available
      if (block.ai_content) {
        this.renderAIContent(block.ai_content, 'Key Insights');
      }
      return;
    }
    
    // Check for data in various formats - direct or nested
    const labels = data?.labels || data?.data?.labels || [];
    const datasets = data?.datasets || data?.data?.datasets || [];
    const hasData = labels.length > 0 && datasets.length > 0 && 
                    datasets.some(ds => ds.data && ds.data.length > 0);
    
    if (!hasData) {
      // No data - show placeholder
      this.doc.setDrawColor(200, 200, 200);
      this.doc.setFillColor(248, 250, 252);
      this.doc.roundedRect(
        this.margins.left,
        this.currentY,
        this.contentWidth,
        60,
        3,
        3,
        'FD'
      );
      this.doc.setFontSize(11);
      this.doc.setTextColor(150, 150, 150);
      this.doc.text(
        'No chart data available',
        this.pageWidth / 2,
        this.currentY + 30,
        { align: 'center' }
      );
      this.currentY += 70;
      return;
    }

    // R2.1: Try server-side rendering first, then client-side fallback
    let chartImage = null;
    
    try {
      // Attempt server-side rendering
      const serverResult = await renderChartServerSide(
        { labels, datasets },
        chartType,
        block.title,
        800,  // width
        400   // height
      );
      
      if (serverResult?.success && serverResult?.image) {
        chartImage = `data:image/png;base64,${serverResult.image}`;
        console.log('Chart rendered server-side successfully');
      }
    } catch (serverErr) {
      console.warn('Server-side chart rendering failed, using client-side fallback:', serverErr.message);
    }
    
    // Fallback to client-side canvas rendering
    if (!chartImage) {
      try {
        chartImage = await this.generateChartImage(data, chartType);
      } catch (clientErr) {
        console.error('Client-side chart rendering also failed:', clientErr);
      }
    }
    
    // Add chart image to PDF
    if (chartImage) {
      try {
        this.doc.addImage(
          chartImage,
          'PNG',
          this.margins.left,
          this.currentY,
          this.contentWidth,
          70
        );
        this.currentY += 80;
      } catch (addErr) {
        console.error('Failed to add chart image to PDF:', addErr);
        this.renderChartAsTable(data, chartType);
      }
    } else {
      // Ultimate fallback to table
      this.renderChartAsTable(data, chartType);
    }
    
    // Add AI insights if available
    if (block.ai_content) {
      this.renderAIContent(block.ai_content, 'Key Insights');
    }
  }

  // Generate chart image using canvas
  async generateChartImage(data, chartType) {
    return new Promise((resolve) => {
      try {
        // Create a temporary canvas
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 400;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          console.warn('Canvas 2D context not available');
          resolve(null);
          return;
        }

        // Fill background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Extract labels and datasets - handle nested data structures
        let labels = data?.labels || [];
        let datasets = data?.datasets || [];
        
        // If data has a 'data' wrapper (from API response), unwrap it
        if (data?.data?.labels) {
          labels = data.data.labels;
          datasets = data.data.datasets || [];
        }
        
        const values = datasets[0]?.data || [];
        
        // Ensure values are valid numbers
        const numericValues = values.map(v => typeof v === 'number' ? v : parseFloat(v) || 0);
        
        if (labels.length === 0 || numericValues.length === 0 || numericValues.every(v => v === 0)) {
          console.warn('No chart data to render:', { labels, values: numericValues });
          resolve(null);
          return;
        }

        const primaryColor = this.branding?.primary_color || '#1e40af';
        const maxValue = Math.max(...numericValues.filter(v => typeof v === 'number' && !isNaN(v)), 1);
      
      // Chart dimensions
      const chartLeft = 80;
      const chartRight = canvas.width - 40;
      const chartTop = 40;
      const chartBottom = canvas.height - 60;
      const chartWidth = chartRight - chartLeft;
      const chartHeight = chartBottom - chartTop;

      // Draw axes
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(chartLeft, chartTop);
      ctx.lineTo(chartLeft, chartBottom);
      ctx.lineTo(chartRight, chartBottom);
      ctx.stroke();

      // Draw Y-axis labels
      ctx.fillStyle = '#6b7280';
      ctx.font = '12px Arial';
      ctx.textAlign = 'right';
      for (let i = 0; i <= 5; i++) {
        const y = chartBottom - (i / 5) * chartHeight;
        const value = (maxValue * i / 5);
        ctx.fillText(this.formatCompactNumber(value), chartLeft - 10, y + 4);
        
        // Grid line
        ctx.strokeStyle = '#f3f4f6';
        ctx.beginPath();
        ctx.moveTo(chartLeft, y);
        ctx.lineTo(chartRight, y);
        ctx.stroke();
      }

      // Draw bars or lines
      const barWidth = Math.min(60, (chartWidth / labels.length) * 0.7);
      const gap = (chartWidth - barWidth * labels.length) / (labels.length + 1);

      if (chartType === 'bar') {
        ctx.fillStyle = primaryColor;
        labels.forEach((label, i) => {
          const x = chartLeft + gap + i * (barWidth + gap);
          const value = numericValues[i] || 0;
          const barHeight = (value / maxValue) * chartHeight;
          const y = chartBottom - barHeight;
          
          // Draw bar
          ctx.fillStyle = primaryColor;
          ctx.fillRect(x, y, barWidth, barHeight);
          
          // Draw label
          ctx.fillStyle = '#374151';
          ctx.font = '11px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(label, x + barWidth / 2, chartBottom + 20);
        });
      } else if (chartType === 'line') {
        ctx.strokeStyle = primaryColor;
        ctx.lineWidth = 3;
        ctx.beginPath();
        
        labels.forEach((label, i) => {
          const x = chartLeft + gap + barWidth / 2 + i * (barWidth + gap);
          const value = numericValues[i] || 0;
          const y = chartBottom - (value / maxValue) * chartHeight;
          
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
          
          // Draw label
          ctx.fillStyle = '#374151';
          ctx.font = '11px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(label, x, chartBottom + 20);
        });
        ctx.stroke();
        
        // Draw points
        ctx.fillStyle = primaryColor;
        labels.forEach((_, i) => {
          const x = chartLeft + gap + barWidth / 2 + i * (barWidth + gap);
          const value = numericValues[i] || 0;
          const y = chartBottom - (value / maxValue) * chartHeight;
          ctx.beginPath();
          ctx.arc(x, y, 5, 0, Math.PI * 2);
          ctx.fill();
        });
      } else if (chartType === 'pie') {
        // Pie chart
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const radius = Math.min(chartWidth, chartHeight) / 2.5;
        const total = numericValues.reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
        
        const colors = ['#1e40af', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'];
        let startAngle = -Math.PI / 2;
        
        numericValues.forEach((value, i) => {
          if (typeof value !== 'number' || value <= 0) return;
          const sliceAngle = (value / total) * Math.PI * 2;
          
          ctx.fillStyle = colors[i % colors.length];
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle);
          ctx.closePath();
          ctx.fill();
          
          startAngle += sliceAngle;
        });
        
        // Legend
        ctx.font = '11px Arial';
        labels.forEach((label, i) => {
          const legendX = chartRight - 120;
          const legendY = chartTop + 20 + i * 20;
          
          ctx.fillStyle = colors[i % colors.length];
          ctx.fillRect(legendX, legendY - 10, 12, 12);
          
          ctx.fillStyle = '#374151';
          ctx.textAlign = 'left';
          ctx.fillText(label, legendX + 18, legendY);
        });
      }

      // Convert to image
      resolve(canvas.toDataURL('image/png'));
      
      } catch (err) {
        console.error('Error generating chart image:', err);
        resolve(null);
      }
    });
  }

  // Format number compactly (1000 -> 1K)
  formatCompactNumber(value) {
    if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
    if (value >= 1000) return (value / 1000).toFixed(1) + 'K';
    return value.toFixed(0);
  }

  // Fallback: Render chart data as table
  renderChartAsTable(data, chartType) {
    // Handle nested data structure
    let labels = data?.labels || [];
    let datasets = data?.datasets || [];
    
    // If data has a 'data' wrapper, unwrap it
    if (data?.data?.labels) {
      labels = data.data.labels;
      datasets = data.data.datasets || [];
    }
    
    const values = datasets[0]?.data || [];
    
    autoTable(this.doc, {
      startY: this.currentY,
      head: [['Category', 'Value']],
      body: labels.map((label, i) => [label, this.formatCurrency(values[i] || 0)]),
      theme: 'striped',
      headStyles: {
        fillColor: this.hexToRgb(this.branding?.primary_color || '#1e40af'),
        textColor: [255, 255, 255],
      },
      margin: { left: this.margins.left, right: this.margins.right },
    });
    
    this.currentY = this.doc.lastAutoTable.finalY + 15;
  }

  // Render page break
  renderPageBreak() {
    this.addPage();
  }

  // Main generate method
  async generate() {
    // Cover page
    this.renderCoverPage();

    // Generate TOC
    const tocBlocks = this.reportData.blocks?.filter(b => b.type !== 'cover_page') || [];
    const toc = this.generateTOC(tocBlocks);
    
    // Check if TOC block exists
    const hasTOCBlock = this.reportData.blocks?.some(b => b.type === 'table_of_contents');
    if (hasTOCBlock && toc.length > 0) {
      this.renderTableOfContents(toc);
    }

    // Render each block
    for (const block of this.reportData.blocks || []) {
      switch (block.type) {
        case 'cover_page':
          // Already rendered
          break;
        case 'table_of_contents':
          // Already rendered
          break;
        case 'page_break':
          this.renderPageBreak();
          break;
        case 'narrative':
          this.renderNarrative(block);
          break;
        case 'data_section':
          this.renderDataTable(block);
          break;
        case 'chart':
          await this.renderChart(block);
          break;
        case 'custom_table':
          this.renderCustomTable(block);
          break;
        default:
          console.warn(`Unknown block type: ${block.type}`);
      }
    }

    // Add final footer
    this.addFooter();

    return this.doc;
  }

  generateTOC(blocks) {
    const toc = [];
    let page = 3; // After cover and TOC pages

    blocks.forEach(block => {
      if (block.type === 'page_break') {
        page++;
        return;
      }

      if (block.title) {
        toc.push({
          title: block.title,
          page,
          type: block.type,
        });
      }

      // Estimate pages
      if (block.type === 'data_section') {
        page += block.config?.detail_level === 'detailed' ? 2 : 1;
      } else if (block.type === 'narrative') {
        page += block.config?.ai_settings?.length === 'detailed' ? 2 : 1;
      } else {
        page += 1;
      }
    });

    return toc;
  }

  // Save/download the PDF
  download(filename = 'report.pdf') {
    this.doc.save(filename);
  }

  // Get as blob
  getBlob() {
    return this.doc.output('blob');
  }
}

// Word Document Generator
export class DOCXGenerator {
  constructor(reportData) {
    this.reportData = reportData;
    this.sections = [];
    this.branding = reportData.branding || {};
  }

  hexToDocxColor(hex) {
    return hex?.replace('#', '') || '1e40af';
  }

  // Create cover page section
  createCoverPage() {
    const { cover_page, branding } = this.reportData;
    if (!cover_page?.enabled) return [];

    const primaryColor = this.hexToDocxColor(branding?.primary_color);

    return [
      new Paragraph({
        children: [],
        spacing: { after: 2000 },
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: branding?.company_name || 'Company',
            size: 28,
            color: primaryColor,
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 1000 },
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: cover_page.title || 'Business Plan',
            bold: true,
            size: 64,
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
      }),
      new Paragraph({
        children: [
          new TextRun({
            text: cover_page.subtitle || '',
            size: 28,
            color: '666666',
          }),
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 },
      }),
      cover_page.show_date
        ? new Paragraph({
            children: [
              new TextRun({
                text: cover_page.date || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                size: 24,
                color: '888888',
              }),
            ],
            alignment: AlignmentType.CENTER,
          })
        : null,
      new Paragraph({
        children: [new PageBreak()],
      }),
    ].filter(Boolean);
  }

  // Create table of contents
  createTableOfContents(toc) {
    const elements = [
      new Paragraph({
        children: [
          new TextRun({
            text: 'Table of Contents',
            bold: true,
            size: 48,
          }),
        ],
        heading: HeadingLevel.HEADING_1,
        spacing: { after: 400 },
      }),
    ];

    toc.forEach(item => {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `${item.title}`,
              size: 24,
            }),
            new TextRun({
              text: `\t${item.page}`,
              size: 24,
            }),
          ],
          spacing: { after: 100 },
        })
      );
    });

    elements.push(
      new Paragraph({
        children: [new PageBreak()],
      })
    );

    return elements;
  }

  // Create section title
  createSectionTitle(title) {
    const primaryColor = this.hexToDocxColor(this.branding?.primary_color);

    return new Paragraph({
      children: [
        new TextRun({
          text: title,
          bold: true,
          size: 36,
          color: primaryColor,
        }),
      ],
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 400, after: 200 },
      border: {
        bottom: {
          color: primaryColor,
          space: 1,
          style: BorderStyle.SINGLE,
          size: 12,
        },
      },
    });
  }

  // Create narrative paragraph
  createNarrative(block) {
    const elements = [];

    if (block.title) {
      elements.push(this.createSectionTitle(block.title));
    }

    // Use AI-generated content first, then fall back to custom content
    const content = block.ai_content || block.data?.content || block.config?.custom_text || 'No content available.';

    // Split into paragraphs
    const paragraphs = content.split('\n\n').filter(p => p.trim());

    paragraphs.forEach(para => {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: para.trim(),
              size: 24,
            }),
          ],
          spacing: { after: 200 },
        })
      );
    });

    return elements;
  }

  // Create AI content paragraph (for descriptions/insights)
  createAIContent(content, label = 'Analysis') {
    if (!content) return [];

    const elements = [];
    
    // Label
    elements.push(
      new Paragraph({
        children: [
          new TextRun({
            text: label,
            italics: true,
            size: 20,
            color: 'B8860B', // Dark golden
          }),
        ],
        spacing: { before: 100, after: 50 },
      })
    );

    // Content paragraphs
    const paragraphs = content.split('\n\n').filter(p => p.trim());
    paragraphs.forEach(para => {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: para.trim(),
              size: 22,
              color: '555555',
            }),
          ],
          spacing: { after: 150 },
        })
      );
    });

    return elements;
  }

  // Create data table
  createDataTable(block) {
    const elements = [];

    if (block.title) {
      elements.push(this.createSectionTitle(block.title));
    }

    // Add AI description if available
    if (block.ai_content) {
      elements.push(...this.createAIContent(block.ai_content, 'Summary'));
    }

    const data = block.data;
    if (!data) {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: 'No data available',
              italics: true,
              color: '888888',
            }),
          ],
        })
      );
      return elements;
    }

    const source = block.config?.source || '';
    const tableData = this.formatDataForTable(data, source);

    if (tableData.headers.length > 0) {
      const primaryColor = this.hexToDocxColor(this.branding?.primary_color);

      // Create header row
      const headerRow = new TableRow({
        children: tableData.headers.map(
          header =>
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: header,
                      bold: true,
                      color: 'FFFFFF',
                      size: 22,
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                }),
              ],
              shading: {
                fill: primaryColor,
                type: ShadingType.CLEAR,
              },
            })
        ),
        tableHeader: true,
      });

      // Create data rows
      const dataRows = tableData.rows.map(
        (row, index) =>
          new TableRow({
            children: row.map(
              cell =>
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: String(cell),
                          size: 20,
                        }),
                      ],
                    }),
                  ],
                  shading:
                    index % 2 === 1
                      ? { fill: 'F8FAFC', type: ShadingType.CLEAR }
                      : undefined,
                })
            ),
          })
      );

      elements.push(
        new Table({
          rows: [headerRow, ...dataRows],
          width: {
            size: 100,
            type: WidthType.PERCENTAGE,
          },
        })
      );

      elements.push(
        new Paragraph({
          children: [],
          spacing: { after: 300 },
        })
      );
    }

    return elements;
  }

  formatDataForTable(data, source) {
    // Handle the new data structure from block_data_service
    if (!data) return { headers: [], rows: [] };
    
    // Handle income statement - new structure
    if (source.includes('income_statement') || source.includes('financial.income_statement')) {
      const lineItems = data.line_items || data.statements || data.summary || [];
      if (Array.isArray(lineItems) && lineItems.length > 0) {
        // Check if it's the new format (line_items with item/amount)
        if (lineItems[0]?.item !== undefined) {
          return {
            headers: ['Item', 'Amount'],
            rows: lineItems.map(item => [
              item.item || item.name || 'Unknown',
              this.formatCurrency(item.amount || 0)
            ])
          };
        }
        // Old format with year-based data
        return {
          headers: ['Year', 'Revenue', 'Gross Profit', 'Net Income', 'Net Margin'],
          rows: lineItems.map(s => [
            s.year || '',
            this.formatCurrency(s.revenue || 0),
            this.formatCurrency(s.gross_profit || 0),
            this.formatCurrency(s.net_income || 0),
            `${s.net_margin || 0}%`,
          ]),
        };
      }
      // Handle summary object
      if (data.summary && typeof data.summary === 'object' && !Array.isArray(data.summary)) {
        return {
          headers: ['Metric', 'Value'],
          rows: [
            ['Revenue', this.formatCurrency(data.summary.revenue || 0)],
            ['Gross Profit', this.formatCurrency(data.summary.gross_profit || 0)],
            ['Net Profit', this.formatCurrency(data.summary.net_profit || 0)],
            ['Gross Margin', `${(data.summary.gross_margin_pct || 0).toFixed(1)}%`],
            ['Net Margin', `${(data.summary.net_margin_pct || 0).toFixed(1)}%`]
          ]
        };
      }
    }

    // Handle balance sheet
    if (source.includes('balance_sheet') || source.includes('financial.balance_sheet')) {
      const sheets = data.balance_sheets || data.summary || [];
      if (Array.isArray(sheets) && sheets.length > 0) {
        return {
          headers: ['Year', 'Total Assets', 'Total Liabilities', 'Total Equity'],
          rows: sheets.map(s => [
            s.year || '',
            this.formatCurrency(s.assets?.total_assets || s.total_assets || 0),
            this.formatCurrency(s.liabilities?.total_liabilities || s.total_liabilities || 0),
            this.formatCurrency(s.equity?.total_equity || s.total_equity || 0),
          ]),
        };
      }
      // Handle object format
      if (data.assets || data.liabilities || data.equity) {
        return {
          headers: ['Category', 'Amount'],
          rows: [
            ['Total Assets', this.formatCurrency(data.assets?.total_assets || 0)],
            ['Total Liabilities', this.formatCurrency(data.liabilities?.total_liabilities || 0)],
            ['Total Equity', this.formatCurrency(data.equity?.total_equity || 0)]
          ]
        };
      }
    }

    // Handle cash flow
    if (source.includes('cash_flow') || source.includes('financial.cash_flow')) {
      const flows = data.cash_flows || [];
      if (Array.isArray(flows) && flows.length > 0) {
        return {
          headers: ['Year', 'Operating', 'Investing', 'Financing', 'Net Change'],
          rows: flows.map(f => [
            f.year || '',
            this.formatCurrency(f.operating_activities || 0),
            this.formatCurrency(f.investing_activities || 0),
            this.formatCurrency(f.financing_activities || 0),
            this.formatCurrency(f.net_change || 0),
          ]),
        };
      }
      // Handle object format
      if (data.operating || data.investing || data.financing) {
        return {
          headers: ['Category', 'Amount'],
          rows: [
            ['Operating Cash Flow', this.formatCurrency(data.operating?.net_operating_cash || data.net_cash_flow || 0)],
            ['Investing Cash Flow', this.formatCurrency(data.investing?.net_investing_cash || 0)],
            ['Financing Cash Flow', this.formatCurrency(data.financing?.net_financing_cash || 0)],
            ['Net Cash Flow', this.formatCurrency(data.net_cash_flow || 0)]
          ]
        };
      }
    }

    // Handle revenue data - new structure
    if (source.includes('revenue') || source.includes('actual_sales') || source.includes('planned_sales')) {
      // Handle periods array
      if (data.periods && Array.isArray(data.periods)) {
        return {
          headers: ['Period', 'Revenue', 'Units Sold'],
          rows: data.periods.map(p => [
            p.period || '',
            this.formatCurrency(p.revenue || 0),
            this.formatNumber(p.units_sold || 0)
          ])
        };
      }
      // Handle totals
      if (data.totals) {
        return {
          headers: ['Metric', 'Value'],
          rows: [
            ['Total Revenue', this.formatCurrency(data.totals.total_revenue || 0)],
            ['Total Units', this.formatNumber(data.totals.total_units || 0)],
            ['Planned Revenue', this.formatCurrency(data.totals.total_planned_revenue || 0)],
            ['Variance %', `${(data.totals.revenue_variance_pct || 0).toFixed(1)}%`]
          ]
        };
      }
    }

    // Handle costs data
    if (source.includes('cost') || source.includes('expense')) {
      // Handle category totals
      if (data.category_totals && Array.isArray(data.category_totals)) {
        return {
          headers: ['Category', 'Total'],
          rows: data.category_totals.map(c => [
            c.category || 'Unknown',
            this.formatCurrency(c.total || c.amount || 0)
          ])
        };
      }
      // Handle by_category
      if (data.by_category && Array.isArray(data.by_category)) {
        return {
          headers: ['Category', 'Amount'],
          rows: data.by_category.map(c => [
            c.category || 'Unknown',
            this.formatCurrency(c.amount || 0)
          ])
        };
      }
    }

    // Handle comparison data
    if (source.includes('comparison') && data.rows) {
      return {
        headers: data.headers || ['Metric', 'Planned', 'Actual', 'Variance', 'Variance %'],
        rows: data.rows.map(r => [
          r.metric || '',
          this.formatCurrency(r.planned || 0),
          this.formatCurrency(r.actual || 0),
          this.formatCurrency(r.variance || 0),
          `${(r.variance_pct || 0).toFixed(1)}%`
        ])
      };
    }

    // Handle KPIs
    if (source.includes('kpis') || source.includes('analytics.kpis')) {
      if (data.metrics && Array.isArray(data.metrics)) {
        return {
          headers: ['KPI', 'Value'],
          rows: data.metrics.map(m => [
            m.name || '',
            m.format === 'percentage' ? `${(m.value || 0).toFixed(1)}%` : 
            m.format === 'currency' ? this.formatCurrency(m.value || 0) : 
            this.formatNumber(m.value || 0)
          ])
        };
      }
    }

    // Handle revenue projections - old format
    if (source.includes('revenue_projections')) {
      const projections = data.projections || [];
      if (Array.isArray(projections) && projections.length > 0) {
        return {
          headers: ['Year', 'Total Revenue'],
          rows: projections.map(p => [p.year || '', this.formatCurrency(p.total_revenue || 0)]),
        };
      }
    }

    // Handle generic array data
    if (Array.isArray(data) && data.length > 0) {
      const firstItem = data[0] || {};
      const headers = Object.keys(firstItem).filter(k => k !== 'id' && k !== '_id');
      return {
        headers,
        rows: data.map(item => headers.map(h => this.formatValue(item[h]))),
      };
    }

    // Handle generic object with arrays
    for (const key of Object.keys(data)) {
      if (Array.isArray(data[key]) && data[key].length > 0) {
        const items = data[key];
        const firstItem = items[0] || {};
        const headers = Object.keys(firstItem).filter(k => k !== 'id' && k !== '_id');
        if (headers.length > 0) {
          return {
            headers,
            rows: items.map(item => headers.map(h => this.formatValue(item[h]))),
          };
        }
      }
    }

    return { headers: [], rows: [] };
  }

  formatNumber(value) {
    if (value === null || value === undefined) return '0';
    return Number(value).toLocaleString();
  }

  formatCurrency(value) {
    if (typeof value !== 'number') return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  }

  formatValue(value) {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'number') return value.toLocaleString();
    return String(value);
  }

  // Create chart with actual image generation
  // R2.1: Uses server-side rendering with client-side fallback
  async createChart(block) {
    const elements = [];

    if (block.title) {
      elements.push(this.createSectionTitle(block.title));
    }

    const chartType = block.config?.chart_type || 'bar';
    const source = block.config?.source || 'data';
    const data = block.data;
    const dataStatus = block.data_status || data?.status;

    // Check data status from backend
    if (dataStatus === 'no_data' || dataStatus === 'error') {
      // Show informative message
      const message = dataStatus === 'error' 
        ? `Chart Error: ${block.error || data?.message || 'Failed to load data'}`
        : `No Data Available: ${block.data_message || data?.message || 'No data available for this chart'}`;
      
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: message,
              italics: true,
              color: dataStatus === 'error' ? 'DC2626' : 'D97706',
              size: 22,
            }),
          ],
          alignment: AlignmentType.CENTER,
          spacing: { before: 200, after: 200 },
          border: {
            top: { style: BorderStyle.SINGLE, size: 1, color: dataStatus === 'error' ? 'FCA5A5' : 'FCD34D' },
            bottom: { style: BorderStyle.SINGLE, size: 1, color: dataStatus === 'error' ? 'FCA5A5' : 'FCD34D' },
            left: { style: BorderStyle.SINGLE, size: 1, color: dataStatus === 'error' ? 'FCA5A5' : 'FCD34D' },
            right: { style: BorderStyle.SINGLE, size: 1, color: dataStatus === 'error' ? 'FCA5A5' : 'FCD34D' },
          },
          shading: { fill: dataStatus === 'error' ? 'FEF2F2' : 'FFFBEB' },
        })
      );
      
      // Still add AI insights if available
      if (block.ai_content) {
        elements.push(...this.createAIContent(block.ai_content, 'Key Insights'));
      }
      
      return elements;
    }

    // Extract labels and datasets
    const labels = data?.labels || data?.data?.labels || [];
    const datasets = data?.datasets || data?.data?.datasets || [];
    const hasData = labels.length > 0 && datasets.length > 0;

    if (!hasData) {
      elements.push(this.createChartPlaceholder(chartType, source));
      return elements;
    }

    // R2.1: Try server-side rendering first
    let chartImageData = null;
    
    try {
      const serverResult = await renderChartServerSide(
        { labels, datasets },
        chartType,
        block.title,
        800,
        400
      );
      
      if (serverResult?.success && serverResult?.image) {
        // Convert base64 to ArrayBuffer for docx
        const binaryString = atob(serverResult.image);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        chartImageData = bytes.buffer;
        console.log('DOCX chart rendered server-side successfully');
      }
    } catch (serverErr) {
      console.warn('Server-side chart rendering failed for DOCX, using client-side fallback:', serverErr.message);
    }
    
    // Fallback to client-side canvas rendering
    if (!chartImageData) {
      try {
        chartImageData = await this.generateChartImageForDocx(data, chartType);
      } catch (clientErr) {
        console.error('Client-side DOCX chart rendering also failed:', clientErr);
      }
    }
    
    if (chartImageData) {
      try {
        // Add the chart image to the document
        elements.push(
          new Paragraph({
            children: [
              new ImageRun({
                data: chartImageData,
                transformation: {
                  width: 550,
                  height: 300,
                },
              }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 200, after: 200 },
          })
        );
      } catch (imgError) {
        console.error('Error adding chart image to DOCX:', imgError);
        elements.push(this.createChartPlaceholder(chartType, source));
        if (data) {
          const tableElements = this.createChartDataTable(data, chartType);
          elements.push(...tableElements);
        }
      }
    } else {
      // Fall back to data table if chart generation fails
      elements.push(this.createChartPlaceholder(chartType, source));
      if (data) {
        const tableElements = this.createChartDataTable(data, chartType);
        elements.push(...tableElements);
      }
    }

    // Add AI insights if available
    if (block.ai_content) {
      elements.push(...this.createAIContent(block.ai_content, 'Key Insights'));
    }

    return elements;
  }

  // Generate chart image data for DOCX (returns base64 buffer)
  async generateChartImageForDocx(data, chartType) {
    return new Promise((resolve) => {
      try {
        // Create a canvas for chart rendering
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 400;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          console.warn('Canvas 2D context not available');
          resolve(null);
          return;
        }

        // Fill white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Extract labels and datasets - handle nested data structures
        let labels = data?.labels || [];
        let datasets = data?.datasets || [];
        
        // If data has a 'data' wrapper, unwrap it
        if (data?.data?.labels) {
          labels = data.data.labels;
          datasets = data.data.datasets || [];
        }
        
        const values = datasets[0]?.data || [];
        const numericValues = values.map(v => typeof v === 'number' ? v : parseFloat(v) || 0);
        
        if (labels.length === 0 || numericValues.length === 0 || numericValues.every(v => v === 0)) {
          console.warn('No chart data to render for DOCX');
          resolve(null);
          return;
        }

        const primaryColor = this.branding?.primary_color || '#1e40af';
        const maxValue = Math.max(...numericValues.filter(v => !isNaN(v)), 1);

        // Chart dimensions
        const chartLeft = 80;
        const chartRight = canvas.width - 40;
        const chartTop = 40;
        const chartBottom = canvas.height - 60;
        const chartWidth = chartRight - chartLeft;
        const chartHeight = chartBottom - chartTop;

        // Draw axes
        ctx.strokeStyle = '#e5e7eb';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(chartLeft, chartTop);
        ctx.lineTo(chartLeft, chartBottom);
        ctx.lineTo(chartRight, chartBottom);
        ctx.stroke();

        // Draw Y-axis labels and grid lines
        ctx.fillStyle = '#6b7280';
        ctx.font = '11px Arial';
        ctx.textAlign = 'right';
        for (let i = 0; i <= 5; i++) {
          const y = chartBottom - (i / 5) * chartHeight;
          const value = (maxValue * i) / 5;
          ctx.fillText(this.formatCompactNumber(value), chartLeft - 10, y + 4);
          
          if (i > 0) {
            ctx.strokeStyle = '#f3f4f6';
            ctx.beginPath();
            ctx.moveTo(chartLeft, y);
            ctx.lineTo(chartRight, y);
            ctx.stroke();
          }
        }

        // Draw chart based on type
        const barWidth = Math.min(60, (chartWidth / labels.length) * 0.7);
        const gap = (chartWidth - barWidth * labels.length) / (labels.length + 1);

        if (chartType === 'bar') {
          ctx.fillStyle = primaryColor;
          labels.forEach((label, i) => {
            const x = chartLeft + gap + i * (barWidth + gap);
            const value = numericValues[i] || 0;
            const barHeight = (value / maxValue) * chartHeight;
            const y = chartBottom - barHeight;
            
            ctx.fillStyle = primaryColor;
            ctx.fillRect(x, y, barWidth, barHeight);
            
            ctx.fillStyle = '#374151';
            ctx.font = '10px Arial';
            ctx.textAlign = 'center';
            // Truncate long labels
            const displayLabel = label.length > 8 ? label.substring(0, 8) + '...' : label;
            ctx.fillText(displayLabel, x + barWidth / 2, chartBottom + 15);
          });
        } else if (chartType === 'line') {
          ctx.strokeStyle = primaryColor;
          ctx.lineWidth = 3;
          ctx.beginPath();
          
          labels.forEach((label, i) => {
            const x = chartLeft + gap + barWidth / 2 + i * (barWidth + gap);
            const value = numericValues[i] || 0;
            const y = chartBottom - (value / maxValue) * chartHeight;
            
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
            
            ctx.fillStyle = '#374151';
            ctx.font = '10px Arial';
            ctx.textAlign = 'center';
            const displayLabel = label.length > 8 ? label.substring(0, 8) + '...' : label;
            ctx.fillText(displayLabel, x, chartBottom + 15);
          });
          ctx.stroke();
          
          // Draw points
          ctx.fillStyle = primaryColor;
          labels.forEach((_, i) => {
            const x = chartLeft + gap + barWidth / 2 + i * (barWidth + gap);
            const value = numericValues[i] || 0;
            const y = chartBottom - (value / maxValue) * chartHeight;
            ctx.beginPath();
            ctx.arc(x, y, 5, 0, Math.PI * 2);
            ctx.fill();
          });
        } else if (chartType === 'pie') {
          const centerX = canvas.width / 2;
          const centerY = canvas.height / 2;
          const radius = Math.min(chartWidth, chartHeight) / 2.5;
          const total = numericValues.reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
          
          const colors = ['#1e40af', '#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#10b981', '#f59e0b'];
          let startAngle = -Math.PI / 2;
          
          numericValues.forEach((value, i) => {
            if (typeof value !== 'number' || value <= 0) return;
            const sliceAngle = (value / total) * Math.PI * 2;
            
            ctx.fillStyle = colors[i % colors.length];
            ctx.beginPath();
            ctx.moveTo(centerX, centerY);
            ctx.arc(centerX, centerY, radius, startAngle, startAngle + sliceAngle);
            ctx.closePath();
            ctx.fill();
            
            startAngle += sliceAngle;
          });
          
          // Legend
          ctx.font = '11px Arial';
          labels.forEach((label, i) => {
            const legendX = chartRight - 150;
            const legendY = chartTop + 20 + i * 22;
            
            ctx.fillStyle = colors[i % colors.length];
            ctx.fillRect(legendX, legendY - 10, 14, 14);
            
            ctx.fillStyle = '#374151';
            ctx.textAlign = 'left';
            const displayLabel = label.length > 15 ? label.substring(0, 15) + '...' : label;
            ctx.fillText(displayLabel, legendX + 20, legendY);
          });
        }

        // Add title if chart type is known
        ctx.fillStyle = '#1f2937';
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(`${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart`, canvas.width / 2, 20);

        // Convert canvas to blob then to ArrayBuffer for docx
        canvas.toBlob((blob) => {
          if (blob) {
            blob.arrayBuffer().then(buffer => {
              resolve(buffer);
            }).catch(() => resolve(null));
          } else {
            resolve(null);
          }
        }, 'image/png');
        
      } catch (err) {
        console.error('Error generating chart image for DOCX:', err);
        resolve(null);
      }
    });
  }

  // Create placeholder for charts when image generation fails
  createChartPlaceholder(chartType, source) {
    return new Paragraph({
      children: [
        new TextRun({
          text: `[${chartType.toUpperCase()} CHART: ${source.replace(/_/g, ' ')}]`,
          italics: true,
          color: '888888',
          size: 24,
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 200, after: 200 },
      border: {
        top: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
        bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
        left: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
        right: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
      },
    });
  }

  // Create a data table from chart data as fallback
  createChartDataTable(data, chartType) {
    const elements = [];
    
    let labels = data?.labels || [];
    let datasets = data?.datasets || [];
    
    if (data?.data?.labels) {
      labels = data.data.labels;
      datasets = data.data.datasets || [];
    }
    
    const values = datasets[0]?.data || [];
    
    if (labels.length === 0) return elements;
    
    const primaryColor = this.hexToDocxColor(this.branding?.primary_color);
    
    const headerRow = new TableRow({
      children: [
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: 'Category', bold: true, color: 'FFFFFF' })] })],
          shading: { fill: primaryColor, type: ShadingType.CLEAR },
        }),
        new TableCell({
          children: [new Paragraph({ children: [new TextRun({ text: 'Value', bold: true, color: 'FFFFFF' })] })],
          shading: { fill: primaryColor, type: ShadingType.CLEAR },
        }),
      ],
    });
    
    const dataRows = labels.map((label, i) => 
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: String(label) })] })] }),
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: this.formatCurrency(values[i] || 0) })] })] }),
        ],
      })
    );
    
    elements.push(
      new Table({
        rows: [headerRow, ...dataRows],
        width: { size: 100, type: WidthType.PERCENTAGE },
      })
    );
    
    elements.push(new Paragraph({ children: [], spacing: { after: 200 } }));
    
    return elements;
  }

  formatCompactNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toFixed(0);
  }

  // Create custom table
  createCustomTable(block) {
    const elements = [];

    if (block.title) {
      elements.push(this.createSectionTitle(block.title));
    }

    // Add AI summary if available
    if (block.ai_content) {
      elements.push(...this.createAIContent(block.ai_content, 'Summary'));
    }

    const columns = block.data?.columns || block.config?.columns || [];
    const rows = block.data?.rows || block.config?.rows || [];

    if (columns.length === 0) {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: 'Empty table',
              italics: true,
              color: '888888',
            }),
          ],
        })
      );
      return elements;
    }

    const primaryColor = this.hexToDocxColor(this.branding?.primary_color);
    const headers = columns.map(c => c.header || c.name || 'Column');

    const headerRow = new TableRow({
      children: headers.map(
        header =>
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: header,
                    bold: true,
                    color: 'FFFFFF',
                  }),
                ],
                alignment: AlignmentType.CENTER,
              }),
            ],
            shading: { fill: primaryColor, type: ShadingType.CLEAR },
          })
      ),
    });

    const dataRows = rows.map(
      row =>
        new TableRow({
          children: columns.map(
            col =>
              new TableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: String(row[col.id] || row[col.name] || ''),
                      }),
                    ],
                  }),
                ],
              })
          ),
        })
    );

    elements.push(
      new Table({
        rows: [headerRow, ...(dataRows.length > 0 ? dataRows : [])],
        width: { size: 100, type: WidthType.PERCENTAGE },
      })
    );

    return elements;
  }

  // Main generate method
  async generate() {
    const children = [];

    // Cover page
    children.push(...this.createCoverPage());

    // Generate TOC
    const tocBlocks = this.reportData.blocks?.filter(b => b.type !== 'cover_page') || [];
    const toc = this.generateTOC(tocBlocks);

    const hasTOCBlock = this.reportData.blocks?.some(b => b.type === 'table_of_contents');
    if (hasTOCBlock && toc.length > 0) {
      children.push(...this.createTableOfContents(toc));
    }

    // Render blocks
    for (const block of this.reportData.blocks || []) {
      switch (block.type) {
        case 'cover_page':
        case 'table_of_contents':
          break;
        case 'page_break':
          children.push(
            new Paragraph({
              children: [new PageBreak()],
            })
          );
          break;
        case 'narrative':
          children.push(...this.createNarrative(block));
          break;
        case 'data_section':
          children.push(...this.createDataTable(block));
          break;
        case 'chart':
          children.push(...(await this.createChart(block)));
          break;
        case 'custom_table':
          children.push(...this.createCustomTable(block));
          break;
        default:
          console.warn(`Unknown block type: ${block.type}`);
      }
    }

    const doc = new Document({
      sections: [
        {
          properties: {},
          children,
        },
      ],
    });

    return doc;
  }

  generateTOC(blocks) {
    const toc = [];
    let page = 3;

    blocks.forEach(block => {
      if (block.type === 'page_break') {
        page++;
        return;
      }

      if (block.title) {
        toc.push({ title: block.title, page, type: block.type });
      }

      if (block.type === 'data_section') {
        page += block.config?.detail_level === 'detailed' ? 2 : 1;
      } else if (block.type === 'narrative') {
        page += block.config?.ai_settings?.length === 'detailed' ? 2 : 1;
      } else {
        page += 1;
      }
    });

    return toc;
  }

  // Download as blob
  async getBlob() {
    const doc = await this.generate();
    return await Packer.toBlob(doc);
  }

  // Download
  async download(filename = 'report.docx') {
    const blob = await this.getBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}

/**
 * Excel Generator
 * Generates Excel (.xlsx) reports with multiple sheets
 */
export class ExcelGenerator {
  constructor(reportData) {
    this.reportData = reportData;
    this.branding = reportData.branding || {};
  }

  async generate() {
    // Dynamic import to avoid SSR issues
    const XLSX = await import('xlsx');
    const workbook = XLSX.utils.book_new();

    // Set workbook properties
    workbook.Props = {
      Title: this.reportData.meta?.plan_name || 'Business Report',
      Subject: 'Financial Report',
      Author: this.branding?.company_name || 'Company',
      CreatedDate: new Date(),
    };

    // Create Summary sheet
    await this.createSummarySheet(workbook, XLSX);

    // Create Financial Data sheet
    await this.createFinancialSheet(workbook, XLSX);

    // Create Charts Data sheet
    await this.createChartsDataSheet(workbook, XLSX);

    // Create detailed sheets for each data section
    await this.createDataSectionSheets(workbook, XLSX);

    return workbook;
  }

  async createSummarySheet(workbook, XLSX) {
    const { meta, blocks, branding } = this.reportData;
    
    const summaryData = [
      ['BUSINESS REPORT SUMMARY'],
      [],
      ['Company', branding?.company_name || 'N/A'],
      ['Plan Name', meta?.plan_name || 'N/A'],
      ['Generated Date', new Date().toLocaleDateString()],
      ['Report Period', meta?.fiscal_year || new Date().getFullYear()],
      [],
      ['REPORT CONTENTS'],
      [],
    ];

    // Add block titles as TOC
    blocks?.forEach((block, index) => {
      if (block.title) {
        summaryData.push([`${index + 1}. ${block.title}`, block.type]);
      }
    });

    // Add executive summary if available
    const execSummary = blocks?.find(b => 
      b.type === 'narrative' && 
      (b.config?.narrative_type === 'executive_summary' || b.title?.toLowerCase().includes('executive'))
    );
    
    if (execSummary?.content) {
      summaryData.push([]);
      summaryData.push(['EXECUTIVE SUMMARY']);
      summaryData.push([execSummary.content]);
    }

    const ws = XLSX.utils.aoa_to_sheet(summaryData);
    
    // Set column widths
    ws['!cols'] = [{ wch: 40 }, { wch: 30 }];
    
    XLSX.utils.book_append_sheet(workbook, ws, 'Summary');
  }

  async createFinancialSheet(workbook, XLSX) {
    const { blocks } = this.reportData;
    
    // Find financial data sections
    const financialBlocks = blocks?.filter(b => 
      b.type === 'data_section' && 
      (b.config?.source?.includes('revenue') || 
       b.config?.source?.includes('cost') || 
       b.config?.source?.includes('profit') ||
       b.config?.source?.includes('financial'))
    ) || [];

    const data = [['FINANCIAL DATA']];
    data.push([]);

    // Collect all financial data
    let hasData = false;
    
    for (const block of financialBlocks) {
      if (block.data && typeof block.data === 'object') {
        hasData = true;
        data.push([block.title || 'Financial Section']);
        data.push([]);
        
        // Handle different data structures
        if (Array.isArray(block.data)) {
          // Array of objects
          if (block.data.length > 0 && typeof block.data[0] === 'object') {
            const headers = Object.keys(block.data[0]);
            data.push(headers);
            block.data.forEach(row => {
              data.push(headers.map(h => this.formatCellValue(row[h])));
            });
          }
        } else if (block.data.labels && block.data.datasets) {
          // Chart data format
          data.push(['Period', ...block.data.datasets.map(ds => ds.label || 'Value')]);
          block.data.labels.forEach((label, i) => {
            const row = [label];
            block.data.datasets.forEach(ds => {
              row.push(this.formatCellValue(ds.data?.[i]));
            });
            data.push(row);
          });
        } else if (typeof block.data === 'object') {
          // Key-value pairs
          Object.entries(block.data).forEach(([key, value]) => {
            if (typeof value !== 'object') {
              data.push([this.formatLabel(key), this.formatCellValue(value)]);
            }
          });
        }
        
        data.push([]);
      }
    }

    if (!hasData) {
      data.push(['No financial data available in this report']);
    }

    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{ wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 20 }];
    
    XLSX.utils.book_append_sheet(workbook, ws, 'Financial Data');
  }

  async createChartsDataSheet(workbook, XLSX) {
    const { blocks } = this.reportData;
    
    // Find chart blocks
    const chartBlocks = blocks?.filter(b => b.type === 'chart') || [];

    const data = [['CHARTS DATA']];
    data.push(['Data extracted from charts for analysis']);
    data.push([]);

    for (const block of chartBlocks) {
      data.push([block.title || 'Chart']);
      data.push([`Chart Type: ${block.config?.chart_type || 'bar'}`]);
      data.push([]);

      // Extract chart data
      const chartData = block.data?.data || block.data;
      
      if (chartData?.labels && chartData?.datasets) {
        // Header row
        const headers = ['Period', ...chartData.datasets.map(ds => ds.label || 'Value')];
        data.push(headers);
        
        // Data rows
        chartData.labels.forEach((label, i) => {
          const row = [label];
          chartData.datasets.forEach(ds => {
            row.push(this.formatCellValue(ds.data?.[i]));
          });
          data.push(row);
        });
        
        // Add totals
        const totals = ['TOTAL'];
        chartData.datasets.forEach(ds => {
          const sum = ds.data?.reduce((a, b) => (parseFloat(a) || 0) + (parseFloat(b) || 0), 0);
          totals.push(this.formatCellValue(sum));
        });
        data.push(totals);
      } else if (block.content) {
        data.push(['Chart rendered as image in PDF/DOCX']);
        data.push([`Source: ${block.config?.source || 'N/A'}`]);
      }
      
      data.push([]);
    }

    if (chartBlocks.length === 0) {
      data.push(['No chart data available in this report']);
    }

    const ws = XLSX.utils.aoa_to_sheet(data);
    ws['!cols'] = [{ wch: 20 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];
    
    XLSX.utils.book_append_sheet(workbook, ws, 'Charts Data');
  }

  async createDataSectionSheets(workbook, XLSX) {
    const { blocks } = this.reportData;
    
    // Find all data sections with substantial data
    const dataSections = blocks?.filter(b => 
      b.type === 'data_section' && b.data && 
      (Array.isArray(b.data) || Object.keys(b.data).length > 0)
    ) || [];

    // Create a sheet for each significant data section
    let sheetCount = 0;
    for (const block of dataSections) {
      if (sheetCount >= 10) break; // Limit to 10 sheets
      
      const sheetName = this.sanitizeSheetName(block.title || `Data ${sheetCount + 1}`);
      const data = [[block.title || 'Data Section']];
      data.push([]);

      // Process data based on structure
      if (Array.isArray(block.data) && block.data.length > 0) {
        if (typeof block.data[0] === 'object') {
          const headers = Object.keys(block.data[0]);
          data.push(headers.map(h => this.formatLabel(h)));
          block.data.forEach(row => {
            data.push(headers.map(h => this.formatCellValue(row[h])));
          });
        } else {
          block.data.forEach(item => data.push([item]));
        }
      } else if (block.data.items) {
        // Handle items array
        data.push(['Item', 'Value']);
        block.data.items.forEach(item => {
          if (typeof item === 'object') {
            data.push([item.name || item.label || '', this.formatCellValue(item.value || item.amount)]);
          } else {
            data.push([item]);
          }
        });
      } else if (typeof block.data === 'object') {
        data.push(['Field', 'Value']);
        Object.entries(block.data).forEach(([key, value]) => {
          if (typeof value !== 'object') {
            data.push([this.formatLabel(key), this.formatCellValue(value)]);
          }
        });
      }

      const ws = XLSX.utils.aoa_to_sheet(data);
      ws['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 20 }];
      
      XLSX.utils.book_append_sheet(workbook, ws, sheetName);
      sheetCount++;
    }
  }

  // Helper: Format cell value
  formatCellValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  // Helper: Format label
  formatLabel(str) {
    if (!str) return '';
    return str
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, s => s.toUpperCase())
      .trim();
  }

  // Helper: Sanitize sheet name
  sanitizeSheetName(name) {
    // Excel sheet names: max 31 chars, no special chars
    return name
      .replace(/[:\\/?*\[\]]/g, '')
      .substring(0, 31)
      .trim();
  }

  // Download
  async download(filename = 'report.xlsx') {
    const XLSX = await import('xlsx');
    const workbook = await this.generate();
    
    // Write workbook to binary
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    
    // Create blob and download
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}

export default { PDFGenerator, DOCXGenerator, ExcelGenerator };
